import { useMutation, useQuery } from '@apollo/client/react';
import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { ConfirmDialog } from '@/components/ConfirmDialog/ConfirmDialog';
import { Icon } from '@/components/Icon/Icon';
import { PermissionGrid } from '@/components/PermissionGrid/PermissionGrid';
import { RoleEditor, type RoleEditorValue } from '@/components/RoleEditor/RoleEditor';
import { Spinner } from '@/components/Spinner/Spinner';
import {
  ASSIGN_ROLE,
  BROKERAGE_MEMBERS_QUERY,
  BROKERAGE_ROLES_QUERY,
  CREATE_ROLE,
  DELETE_ROLE,
  RENAME_ROLE,
  SET_ROLE_GRANTS,
  UNASSIGN_ROLE,
  type BrokerageMembersResult,
  type BrokerageRole,
  type BrokerageRolesResult,
} from '@/graphql/brokerages';
import { roleChanges, sameGrants, toGrantInputs } from '@/lib/brokerage-roles';
import { memberLabel } from '@/lib/brokerages';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { useToast } from '@/toast/useToast';

import styles from '../BrokeragesContainer.module.css';
import type { SectionProps } from './MembersSection';

/** A brokerage's roles as a grid in plain words, and the editor that changes them. */
export function RolesSection({
  tenantId,
  canEdit,
  canReadMembers,
  meId,
  onAccessChanged,
}: SectionProps & { canReadMembers: boolean; meId: string | null }) {
  const toast = useToast();
  const roles = useQuery<BrokerageRolesResult>(BROKERAGE_ROLES_QUERY, {
    variables: { tenantId },
    fetchPolicy: 'cache-and-network',
  });
  const members = useQuery<BrokerageMembersResult>(BROKERAGE_MEMBERS_QUERY, {
    variables: { tenantId },
    skip: !canReadMembers,
  });
  const [createRole] = useMutation<{ createRole: BrokerageRole }>(CREATE_ROLE);
  const [renameRole] = useMutation(RENAME_ROLE);
  const [setRoleGrants] = useMutation(SET_ROLE_GRANTS);
  const [assignRole] = useMutation(ASSIGN_ROLE);
  const [unassignRole] = useMutation(UNASSIGN_ROLE);
  const [deleteRole] = useMutation(DELETE_ROLE);

  const [editing, setEditing] = useState<BrokerageRole | 'new' | null>(null);
  const [deleting, setDeleting] = useState<BrokerageRole | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const memberOptions = (members.data?.brokerage.members ?? [])
    .filter((member) => !member.deletedAt && member.personId && member.personId !== meId)
    .map((member) => ({ personId: member.personId as string, label: memberLabel(member, meId) }));

  const run = async (call: () => Promise<unknown>, success: string) => {
    setBusy(true);
    setError(null);
    try {
      await call();
      toast.success({ title: success });
      return true;
    } catch (cause) {
      setError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        onAccessChanged();
      }
      return false;
    } finally {
      setBusy(false);
      void roles.refetch();
    }
  };

  const onSave = async (value: RoleEditorValue) => {
    const role = editing === 'new' ? null : editing;
    const ok = await run(
      async () => {
        let roleId = role?.id;
        if (!role) {
          const { data } = await createRole({
            variables: { tenantId, name: value.name, grants: toGrantInputs(value.grants) },
          });
          roleId = data?.createRole.id;
        } else {
          if (role.presetKey === null && value.name !== role.name) {
            await renameRole({ variables: { roleId, name: value.name } });
          }
          if (!sameGrants(role.grants, value.grants)) {
            await setRoleGrants({ variables: { roleId, grants: toGrantInputs(value.grants) } });
          }
        }
        if (roleId && (!role || role.presetKey === null)) {
          const { add, remove } = roleChanges(role?.assigneeIds ?? [], value.assigneeIds);
          for (const personId of add) {
            await assignRole({ variables: { roleId, personId } });
          }
          for (const personId of remove) {
            await unassignRole({ variables: { roleId, personId } });
          }
        }
      },
      role ? `${value.name} saved` : `${value.name} created`,
    );
    if (ok) {
      setEditing(null);
    }
  };

  const onDelete = async () => {
    if (
      deleting &&
      (await run(
        () => deleteRole({ variables: { roleId: deleting.id } }),
        `${deleting.name} deleted`,
      ))
    ) {
      setDeleting(null);
      setEditing(null);
    }
  };

  const rows = roles.data?.roles ?? [];

  return (
    <section aria-label="Roles and permissions" className={styles.section}>
      <div className={styles.sectionHead}>
        <p className={styles.sectionLede}>
          Every role in this brokerage and what it lets people do. Each member holds one main role,
          and can hold extra roles this brokerage has made.
        </p>
        {canEdit ? (
          <Button
            icon={<Icon name="plus" />}
            onClick={() => {
              setError(null);
              setEditing('new');
            }}
          >
            New role
          </Button>
        ) : null}
      </div>
      <Callout title="Where these permissions apply">
        Keystone uses them straight away. The main app follows them only for teams where Keystone
        permissions are switched on; elsewhere it still decides from each member’s main role.
      </Callout>
      {roles.error && !roles.data ? (
        <Callout tone="danger" title="Roles couldn't be loaded">
          {friendlyError(roles.error)}
        </Callout>
      ) : roles.loading && !roles.data ? (
        <p className={styles.loading} role="status">
          <Spinner /> Loading roles…
        </p>
      ) : (
        <PermissionGrid
          canEdit={canEdit}
          onEditRole={(role) => {
            setError(null);
            setEditing(role);
          }}
          roles={rows}
        />
      )}

      {editing ? (
        <RoleEditor
          busy={busy}
          error={error}
          members={memberOptions}
          onCancel={() => setEditing(null)}
          onDelete={editing !== 'new' ? () => setDeleting(editing) : undefined}
          onSubmit={onSave}
          role={editing === 'new' ? null : editing}
        />
      ) : null}

      {deleting ? (
        <ConfirmDialog
          busy={busy}
          confirmLabel="Delete role"
          error={error}
          onCancel={() => setDeleting(null)}
          onConfirm={onDelete}
          title={`Delete ${deleting.name ?? 'this role'}?`}
        >
          Everyone who holds it loses what it allows. Their main role is not affected.
        </ConfirmDialog>
      ) : null}
    </section>
  );
}
